import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useRef,
  useCallback,
  ReactNode,
} from 'react'

// --- Types ---

export interface GstinInfo {
  gstin: string
  status: string
  state: string
}

export interface BusinessDetails {
  legalName: string | null
  tradeName: string | null
  constitutionOfBusiness: string | null
  gstinStatus: string | null
  taxpayerType: string | null
  dateOfRegistration: string | null
  lastUpdateDate: string | null
  natureOfBusinessActivities: string[] | null
  principalPlaceAddress: string | null
  principalPlaceSplitAddress: {
    buildingName?: string
    street?: string
    location?: string
    buildingNumber?: string
    district?: string
    state?: string
    city?: string
    flatNumber?: string
    pincode?: string
  } | null
  centerJurisdiction: string | null
  stateJurisdiction: string | null
}

export interface CrossIdentifierData {
  pan: string | null
  gstinList: GstinInfo[] | null
  udyamList: string[] | null
  fetchedFrom: 'gstin' | 'pan' | 'udyam' | 'cin'
}

export interface OnboardingState {
  currentStep: number
  completedSteps: Set<number>
  stepsRequiringRevalidation: Set<number>
  lastActivityTimestamp: number

  // Step 1
  businessType: string | null

  // Step 2
  identifierType: 'gstin' | 'pan' | 'udyam' | 'cin' | null
  identifierValue: string
  secondaryIdentifierType: 'cin' | 'udyam' | null
  secondaryIdentifierValue: string
  verificationStatus: 'idle' | 'loading' | 'success' | 'error'
  verifiedBusinessName: string | null
  verifiedAddress: string | null
  verificationError: string | null
  verifiedBusinessDetails: BusinessDetails | null
  crossIdentifiers: CrossIdentifierData | null
  retryCount: number

  // Step 3
  selectedIndustryCluster: string | null
  selectedIndustry: string | null
  industrySearchQuery: string

  // Step 4
  userName: string
  userEmail: string
  userPassword: string
}

// --- Actions ---

type OnboardingAction =
  | { type: 'SET_BUSINESS_TYPE'; payload: string }
  | {
      type: 'SET_IDENTIFIER'
      payload: { identifierType: 'gstin' | 'pan' | 'udyam' | 'cin' | null; identifierValue: string }
    }
  | {
      type: 'SET_SECONDARY_IDENTIFIER'
      payload: { secondaryIdentifierType: 'cin' | 'udyam' | null; secondaryIdentifierValue: string }
    }
  | {
      type: 'SET_VERIFICATION_RESULT'
      payload: {
        status: 'idle' | 'loading' | 'success' | 'error'
        businessName?: string | null
        address?: string | null
        error?: string | null
        businessDetails?: BusinessDetails | null
      }
    }
  | { type: 'SET_CROSS_IDENTIFIERS'; payload: CrossIdentifierData | null }
  | {
      type: 'SET_INDUSTRY'
      payload: {
        cluster: string | null
        industry: string | null
        searchQuery?: string
      }
    }
  | {
      type: 'SET_USER_DETAILS'
      payload: { userName?: string; userEmail?: string; userPassword?: string }
    }
  | { type: 'SET_STEP'; payload: number }
  | { type: 'MARK_STEP_COMPLETE'; payload: number }
  | { type: 'INVALIDATE_STEPS'; payload: number[] }
  | { type: 'RESET_STATE' }
  | { type: 'INCREMENT_RETRY' }

// --- Initial State ---

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000 // 30 minutes

function createInitialState(): OnboardingState {
  return {
    currentStep: 1,
    completedSteps: new Set<number>(),
    stepsRequiringRevalidation: new Set<number>(),
    lastActivityTimestamp: Date.now(),

    // Step 1
    businessType: null,

    // Step 2
    identifierType: null,
    identifierValue: '',
    secondaryIdentifierType: null,
    secondaryIdentifierValue: '',
    verificationStatus: 'idle',
    verifiedBusinessName: null,
    verifiedAddress: null,
    verificationError: null,
    verifiedBusinessDetails: null,
    crossIdentifiers: null,
    retryCount: 0,

    // Step 3
    selectedIndustryCluster: null,
    selectedIndustry: null,
    industrySearchQuery: '',

    // Step 4
    userName: '',
    userEmail: '',
    userPassword: '',
  }
}

// --- Reducer ---

function onboardingReducer(state: OnboardingState, action: OnboardingAction): OnboardingState {
  const updatedTimestamp = Date.now()

  switch (action.type) {
    case 'SET_BUSINESS_TYPE':
      return {
        ...state,
        businessType: action.payload,
        lastActivityTimestamp: updatedTimestamp,
      }

    case 'SET_IDENTIFIER':
      return {
        ...state,
        identifierType: action.payload.identifierType,
        identifierValue: action.payload.identifierValue,
        lastActivityTimestamp: updatedTimestamp,
      }

    case 'SET_SECONDARY_IDENTIFIER':
      return {
        ...state,
        secondaryIdentifierType: action.payload.secondaryIdentifierType,
        secondaryIdentifierValue: action.payload.secondaryIdentifierValue,
        lastActivityTimestamp: updatedTimestamp,
      }

    case 'SET_VERIFICATION_RESULT':
      return {
        ...state,
        verificationStatus: action.payload.status,
        verifiedBusinessName:
          action.payload.businessName !== undefined
            ? action.payload.businessName
            : state.verifiedBusinessName,
        verifiedAddress:
          action.payload.address !== undefined ? action.payload.address : state.verifiedAddress,
        verificationError:
          action.payload.error !== undefined ? action.payload.error : state.verificationError,
        verifiedBusinessDetails:
          action.payload.businessDetails !== undefined
            ? action.payload.businessDetails
            : state.verifiedBusinessDetails,
        lastActivityTimestamp: updatedTimestamp,
      }

    case 'SET_CROSS_IDENTIFIERS':
      return {
        ...state,
        crossIdentifiers: action.payload,
        lastActivityTimestamp: updatedTimestamp,
      }

    case 'SET_INDUSTRY':
      return {
        ...state,
        selectedIndustryCluster: action.payload.cluster,
        selectedIndustry: action.payload.industry,
        industrySearchQuery:
          action.payload.searchQuery !== undefined
            ? action.payload.searchQuery
            : state.industrySearchQuery,
        lastActivityTimestamp: updatedTimestamp,
      }

    case 'SET_USER_DETAILS':
      return {
        ...state,
        userName: action.payload.userName !== undefined ? action.payload.userName : state.userName,
        userEmail:
          action.payload.userEmail !== undefined ? action.payload.userEmail : state.userEmail,
        userPassword:
          action.payload.userPassword !== undefined
            ? action.payload.userPassword
            : state.userPassword,
        lastActivityTimestamp: updatedTimestamp,
      }

    case 'SET_STEP':
      return {
        ...state,
        currentStep: action.payload,
        lastActivityTimestamp: updatedTimestamp,
      }

    case 'MARK_STEP_COMPLETE': {
      const newCompletedSteps = new Set(state.completedSteps)
      newCompletedSteps.add(action.payload)
      return {
        ...state,
        completedSteps: newCompletedSteps,
        lastActivityTimestamp: updatedTimestamp,
      }
    }

    case 'INVALIDATE_STEPS': {
      const newRevalidation = new Set(state.stepsRequiringRevalidation)
      for (const step of action.payload) {
        newRevalidation.add(step)
      }
      // Remove invalidated steps from completedSteps
      const newCompleted = new Set(state.completedSteps)
      for (const step of action.payload) {
        newCompleted.delete(step)
      }
      return {
        ...state,
        completedSteps: newCompleted,
        stepsRequiringRevalidation: newRevalidation,
        lastActivityTimestamp: updatedTimestamp,
      }
    }

    case 'RESET_STATE':
      return createInitialState()

    case 'INCREMENT_RETRY':
      return {
        ...state,
        retryCount: state.retryCount + 1,
        lastActivityTimestamp: updatedTimestamp,
      }

    default:
      return state
  }
}

// --- Context ---

interface OnboardingContextType {
  state: OnboardingState
  dispatch: React.Dispatch<OnboardingAction>
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined)

// --- Provider ---

interface OnboardingProviderProps {
  children: ReactNode
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const [state, dispatch] = useReducer(onboardingReducer, undefined, createInitialState)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // 30-minute inactivity timeout check
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const now = Date.now()
      const elapsed = now - state.lastActivityTimestamp
      if (elapsed >= INACTIVITY_TIMEOUT_MS) {
        dispatch({ type: 'RESET_STATE' })
      }
    }, 60_000) // Check every minute

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [state.lastActivityTimestamp])

  const value: OnboardingContextType = { state, dispatch }

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
}

// --- Hook ---

export function useOnboarding() {
  const context = useContext(OnboardingContext)
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider')
  }
  return context
}

// --- Exported for testing ---
export { onboardingReducer, createInitialState, INACTIVITY_TIMEOUT_MS }
export type { OnboardingAction }
